import {
    AppShell,
    Group,
    Text, Image,
    Menu,
    ActionIcon,
    Button,
    Drawer,
    Stack,
    Container,
    Badge,
    useMantineTheme,
    Box,
    Divider
} from '@mantine/core'
import {
    IconSearch,
    IconHeart,
    IconSettings,
    IconLogout,
    IconLogin, IconShield,
    IconDeviceGamepad2,
    IconSun,
    IconMoon,
    IconBell,
    IconBuildingStore,
    IconMenu2
} from '@tabler/icons-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useI18n } from '@/hooks/useI18n'
import { platformsApi } from '../../services/api'
import { useAuth } from '@/hooks/useAuth'
import { useSpotlight } from '@/hooks/useSpotlight'
import { useDarkMode } from '@/hooks/useDarkMode'
import { useNotifications } from '@/hooks/useNotifications'
import { useMediaQuery } from '@mantine/hooks'
import { useBranding } from '@/hooks/useBranding'

// Reusable icon component to reduce color logic duplication
const ThemedIcon = ({ icon: Icon, size = 20 }: { icon: any, size?: number }) => {
    const { colorScheme } = useDarkMode()
    const color = colorScheme === 'dark' ? 'var(--mantine-color-customGreen-2)' : 'var(--mantine-color-customGreen-6)'
    return <Icon size={size} color={color} />
}

// Sub-header component for browse navigation
const SubHeader = () => {
    const { t } = useI18n()
    const { colorScheme } = useDarkMode()
    const theme = useMantineTheme()
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
    const isTablet = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

    const { data: platforms } = useQuery({
        queryKey: ['platforms-subheader'],
        queryFn: () => platformsApi.getPlatforms(1, 100),
        select: (response) => response.data.data,
    })

    if (isMobile) return null

    // Limit platforms on tablet to prevent wrapping
    const displayPlatforms = isTablet ? platforms?.slice(0, 4) : platforms

    return (
        <Box
            style={{
                backgroundColor: colorScheme === 'dark'
                    ? 'rgba(34, 197, 94, 0.03)'
                    : 'rgba(34, 197, 94, 0.02)',
                backdropFilter: 'blur(8px)',
                marginBottom: '0px',
                marginTop: '4px',
            }}
        >
            <Container size="xl" py={isTablet ? 12 : 16}>
                <Group
                    gap={isTablet ? "xs" : "md"}
                    justify="space-between"
                    align="center"
                    wrap="nowrap"
                >
                    {/* Platform navigation */}
                    <Group gap={isTablet ? "xs" : "md"} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        {displayPlatforms?.map((platform) => (
                            <Button
                                key={platform.slug}
                                variant="subtle"
                                color="customGreen"
                                component={Link}
                                to={`/platform/${platform.slug}`}
                                size={isTablet ? "xs" : "sm"}
                                style={{
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    fontSize: isTablet ? '12px' : '14px',
                                    padding: isTablet ? '4px 8px' : '6px 12px',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    '&:hover': {
                                        backgroundColor: colorScheme === 'dark'
                                            ? 'rgba(34, 197, 94, 0.1)'
                                            : 'rgba(34, 197, 94, 0.05)',
                                    }
                                }}
                            >
                                {platform.name}
                            </Button>
                        ))}
                    </Group>

                    {/* Main navigation items */}
                    <Group gap={isTablet ? "xs" : "md"} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Button
                            variant="subtle"
                            color="customGreen"
                            component={Link}
                            to="/games"
                            size={isTablet ? "xs" : "sm"}
                            style={{
                                borderRadius: '8px',
                                fontWeight: 500,
                                fontSize: isTablet ? '12px' : '14px',
                                padding: isTablet ? '4px 8px' : '6px 12px',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    backgroundColor: colorScheme === 'dark'
                                        ? 'rgba(34, 197, 94, 0.1)'
                                        : 'rgba(34, 197, 94, 0.05)',
                                }
                            }}
                        >
                            {t('nav.games')}
                        </Button>

                        <Button
                            variant="subtle"
                            color="customGreen"
                            component={Link}
                            to="/stores"
                            size={isTablet ? "xs" : "sm"}
                            style={{
                                borderRadius: '8px',
                                fontWeight: 500,
                                fontSize: isTablet ? '12px' : '14px',
                                padding: isTablet ? '4px 8px' : '6px 12px',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                    backgroundColor: colorScheme === 'dark'
                                        ? 'rgba(34, 197, 94, 0.1)'
                                        : 'rgba(34, 197, 94, 0.05)',
                                }
                            }}
                        >
                            {t('nav.stores')}
                        </Button>
                    </Group>
                </Group>
            </Container>
        </Box>
    )
}

export const Header = () => {
    const navigate = useNavigate()
    const { user, logout, isAdmin } = useAuth()
    const { t, country, setLanguage, setCountry } = useI18n()
    const { open: openSpotlight } = useSpotlight()
    const { colorScheme, toggleColorScheme } = useDarkMode()
    const { unreadCount } = useNotifications()
    const [drawerOpened, setDrawerOpened] = useState(false)
    const theme = useMantineTheme()
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
    const isSmallMobile = useMediaQuery(`(max-width: 480px)`)
    const { siteName, getTaglineForCountry, logo } = useBranding()

    const { data: platforms } = useQuery({
        queryKey: ['platforms'],
        queryFn: () => platformsApi.getPlatforms(1, 100),
        select: (response) => response.data.data,
    })

    const handleLogout = async () => {
        await logout()
        navigate('/')
    }

    // Get current country flag
    const getCurrentCountryFlag = () => {
        const flags = { NL: '🇳🇱', BE: '🇧🇪', US: '🇺🇸' }
        return flags[country as keyof typeof flags] || '🇳🇱'
    }

    return (
        <>
            <AppShell.Header
                p={0}
                style={{
                    height: 'auto',
                    border: 'none',
                    backdropFilter: 'blur(10px)',
                    backgroundColor: colorScheme === 'dark'
                        ? 'rgba(26, 27, 30, 0.4)'
                        : 'rgba(255, 255, 255, 0.4)',
                }}
            >
                {/* Main Header */}
                <Container
                    size="xl"
                    py={isSmallMobile ? "xs" : "sm"}
                    pb={0}
                >
                    <Group justify="space-between" align="center" h="100%">
                        {/* Logo and Brand Section */}
                        {isMobile ? (
                            // Mobile layout: Stack logo+text and tagline vertically
                            <Stack gap={2} align="flex-start" style={{ flexShrink: 0 }}>
                                {/* Logo and Spellen.land text - horizontal */}
                                <Group gap={isSmallMobile ? "xs" : "md"} align="center">
                                    <Link to="/" style={{ display: 'flex', alignItems: 'center' }} title='Home'>
                                        <Box
                                            style={{
                                                position: 'relative',
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.05)'
                                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)'
                                                e.currentTarget.style.boxShadow = 'none'
                                            }}
                                        >
                                            <Image
                                                src={logo.white}
                                                w={{ base: isSmallMobile ? 40 : 60, sm: 60 }}
                                                h={{ base: isSmallMobile ? 40 : 60, sm: 60 }}
                                                radius="md"
                                                style={{
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    flexShrink: 0
                                                }}
                                            />
                                        </Box>
                                    </Link>

                                    <Text
                                        fw={600}
                                        size="xl"
                                        c={colorScheme === 'dark' ? 'customGreen.2' : 'customGreen.6'}
                                        style={{
                                            fontSize: isSmallMobile
                                                ? 'clamp(1rem, 3vw, 1.5rem)'
                                                : 'clamp(1.2rem, 2.5vw, 2.3rem)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontFamily: '"Poppins", sans-serif',
                                            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                                            lineHeight: 1.1
                                        }}
                                    >
                                        {siteName}
                                    </Text>
                                </Group>

                                {/* Tagline underneath on mobile */}
                                <Text
                                    size="xs"
                                    c="dimmed"
                                    style={{
                                        fontSize: isSmallMobile ? '10px' : '12px',
                                        fontWeight: 400,
                                        letterSpacing: '0.02em',
                                        lineHeight: 1.2,
                                        textAlign: 'left'
                                    }}
                                >
                                    {getTaglineForCountry(country)}
                                </Text>
                            </Stack>
                        ) : (
                            // Desktop layout: Original horizontal layout
                            <Group gap={isSmallMobile ? "xs" : "md"} align="center">
                                <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        style={{
                                            position: 'relative',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)'
                                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)'
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)'
                                            e.currentTarget.style.boxShadow = 'none'
                                        }}
                                    >
                                        <Image
                                            src={logo.white}
                                            w={{ base: isSmallMobile ? 40 : 60, sm: 60 }}
                                            h={{ base: isSmallMobile ? 40 : 60, sm: 60 }}
                                            radius="md"
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                flexShrink: 0
                                            }}
                                        />
                                    </Box>
                                </Link>

                                <Stack gap={0} style={{ flexShrink: 0 }}>
                                    <Text
                                        fw={600}
                                        size="xl"
                                        c={colorScheme === 'dark' ? 'customGreen.2' : 'customGreen.6'}
                                        style={{
                                            fontSize: isSmallMobile
                                                ? 'clamp(1rem, 3vw, 1.5rem)'
                                                : 'clamp(1.2rem, 2.5vw, 2.3rem)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontFamily: '"Poppins", sans-serif',
                                            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                                            lineHeight: 1.1
                                        }}
                                    >
                                        {siteName}
                                    </Text>
                                    <Text
                                        size="xs"
                                        c="dimmed"
                                        style={{
                                            fontSize: isSmallMobile ? '10px' : '12px',
                                            fontWeight: 400,
                                            letterSpacing: '0.02em',
                                            lineHeight: 1.2,
                                            marginTop: '-2px'
                                        }}
                                    >
                                        {getTaglineForCountry(country)}
                                    </Text>
                                </Stack>
                            </Group>
                        )}

                        {/* Desktop Actions */}
                        <Group gap={isSmallMobile ? "xs" : "lg"} align="center">
                            {/* Country/Language - Simplified */}
                            {!isMobile && (
                                <Menu shadow="lg" width={220} position="bottom-end">
                                    <Menu.Target>
                                        <ActionIcon
                                            size={isSmallMobile ? "md" : "lg"}
                                            variant="light"
                                            color="customGreen"
                                            title={`${getCurrentCountryFlag()} ${t('countrySelector.label')}`}
                                            style={{
                                                borderRadius: '10px',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <span style={{ fontSize: isSmallMobile ? '14px' : '18px' }}>{getCurrentCountryFlag()}</span>
                                        </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown style={{ borderRadius: '12px', padding: '8px' }}>
                                        <Menu.Label style={{ fontWeight: 600, fontSize: '14px' }}>
                                            {t('menu.countries')}
                                        </Menu.Label>
                                        <Menu.Item
                                            onClick={() => setCountry('NL')}
                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                        >
                                            🇳🇱 {t('country.NL')}
                                        </Menu.Item>
                                        <Menu.Item
                                            onClick={() => setCountry('BE')}
                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                        >
                                            🇧🇪 {t('country.BE')}
                                        </Menu.Item>
                                        <Menu.Item
                                            onClick={() => setCountry('NL')}
                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                        >
                                            🇺🇸 {t('country.US')}
                                        </Menu.Item>
                                        <Divider my="xs" />
                                        <Menu.Label style={{ fontWeight: 600, fontSize: '14px' }}>
                                            {t('menu.languages')}
                                        </Menu.Label>
                                        <Menu.Item
                                            onClick={() => setLanguage('en')}
                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                        >
                                            🇺🇸 English
                                        </Menu.Item>
                                        <Menu.Item
                                            onClick={() => setLanguage('nl')}
                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                        >
                                            🇳🇱 Nederlands
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            )}

                            {/* Dark Mode Toggle */}
                            <ActionIcon
                                size={isSmallMobile ? "md" : "lg"}
                                variant="light"
                                color="customGreen"
                                onClick={toggleColorScheme}
                                title={colorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                style={{
                                    borderRadius: '10px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {colorScheme === 'dark' ? <IconSun size={isSmallMobile ? 16 : 20} /> : <IconMoon size={isSmallMobile ? 16 : 20} />}
                            </ActionIcon>

                            {/* Search */}
                            <ActionIcon
                                size={isSmallMobile ? "md" : "lg"}
                                variant="light"
                                color="customGreen"
                                onClick={openSpotlight}
                                style={{
                                    borderRadius: '10px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <ThemedIcon icon={IconSearch} size={isSmallMobile ? 16 : 20} />
                            </ActionIcon>

                            {/* Notifications */}
                            {user && !isMobile && (
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <ActionIcon
                                        size={isSmallMobile ? "md" : "lg"}
                                        variant="light"
                                        color="customGreen"
                                        onClick={() => navigate('/notifications')}
                                        style={{
                                            position: 'relative',
                                            zIndex: 2,
                                            borderRadius: '10px',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <ThemedIcon icon={IconBell} size={isSmallMobile ? 16 : 20} />
                                    </ActionIcon>
                                    {unreadCount > 0 && (
                                        <Badge
                                            size="xs"
                                            variant="filled"
                                            color="red"
                                            style={{
                                                position: 'absolute',
                                                top: '-4px',
                                                right: '-4px',
                                                minWidth: '20px',
                                                height: '20px',
                                                fontSize: '11px',
                                                zIndex: 1,
                                                pointerEvents: 'none',
                                                borderRadius: '10px',
                                                fontWeight: 600
                                            }}
                                        >
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </Badge>
                                    )}
                                </div>
                            )}

                            {/* User Menu */}
                            {user ? (
                                <>
                                    {!isMobile && (
                                        <Menu shadow="lg" width={220} position="bottom-end">
                                            <Menu.Target>
                                                <ActionIcon
                                                    size={isSmallMobile ? "md" : "lg"}
                                                    variant="light"
                                                    color="customGreen"
                                                    style={{
                                                        borderRadius: '10px',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <ThemedIcon icon={IconSettings} size={isSmallMobile ? 16 : 20} />
                                                </ActionIcon>
                                            </Menu.Target>

                                            <Menu.Dropdown style={{ borderRadius: '12px', padding: '8px' }}>
                                                <Menu.Item
                                                    leftSection={<ThemedIcon icon={IconHeart} size={16} />}
                                                    onClick={() => navigate('/wishlist')}
                                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                                >
                                                    {t('nav.wishlist')}
                                                </Menu.Item>

                                                <Menu.Item
                                                    leftSection={<ThemedIcon icon={IconSettings} size={16} />}
                                                    onClick={() => navigate('/settings')}
                                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                                >
                                                    {t('nav.settings')}
                                                </Menu.Item>

                                                {isAdmin && (
                                                    <>
                                                        <Divider my="xs" />
                                                        <Menu.Item
                                                            leftSection={<ThemedIcon icon={IconShield} size={16} />}
                                                            onClick={() => navigate('/admin')}
                                                            style={{ borderRadius: '8px', margin: '2px 0' }}
                                                        >
                                                            {t('nav.admin')}
                                                        </Menu.Item>
                                                    </>
                                                )}

                                                <Divider my="xs" />

                                                <Menu.Item
                                                    leftSection={<IconLogout size={16} color="red" />}
                                                    onClick={handleLogout}
                                                    color="red"
                                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                                >
                                                    {t('nav.logout')}
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                        </Menu>
                                    )}
                                </>
                            ) : (
                                <Group gap="xs" visibleFrom="sm">
                                    <Button
                                        variant="gradient"
                                        gradient={{ from: 'customGreen', to: 'customGreen', deg: 135 }}
                                        leftSection={<IconLogin size={16} color="white" />}
                                        onClick={() => {
                                            navigate('/login')
                                            setDrawerOpened(false)
                                        }}
                                        size="sm"
                                        style={{
                                            borderRadius: '10px',
                                            fontWeight: 500,
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                                        }}
                                    >
                                        {t('auth.loginOrSignup')}
                                    </Button>
                                </Group>
                            )}

                            {/* Mobile Menu Button */}
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <ActionIcon
                                    size={isSmallMobile ? "md" : "lg"}
                                    variant="light"
                                    ml={16}
                                    color="customGreen"
                                    onClick={() => setDrawerOpened((o) => !o)}
                                    title="Menu"
                                    hiddenFrom="sm"
                                    style={{
                                        borderRadius: '10px',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <IconMenu2 size={isSmallMobile ? 16 : 20} />
                                </ActionIcon>
                                {user && isMobile && unreadCount > 0 && (
                                    <Badge
                                        size="xs"
                                        variant="filled"
                                        color="red"
                                        style={{
                                            position: 'absolute',
                                            top: '-4px',
                                            right: '-4px',
                                            minWidth: '20px',
                                            height: '20px',
                                            fontSize: '11px',
                                            zIndex: 1,
                                            pointerEvents: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 600
                                        }}
                                    >
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </Badge>
                                )}
                            </div>
                        </Group>
                    </Group>
                </Container>

                {/* Sub-header for browse navigation */}
                <SubHeader />
            </AppShell.Header>

            {/* Mobile Drawer - Modernized */}
            <Drawer
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
                size="xs"
                padding="md"
                title={
                    <Text fw={600} size="md" style={{
                        fontFamily: '"Poppins", sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        background: colorScheme === 'dark'
                            ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
                            : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        lineHeight: 1.2
                    }}>
                        Navigation
                    </Text>
                }
                hiddenFrom="sm"
                styles={{
                    header: {
                        borderBottom: `1px solid ${colorScheme === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)'}`,
                        padding: '16px 20px',
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center'
                    },
                    body: {
                        padding: '16px',
                        maxHeight: 'calc(100vh - 120px)',
                        overflowY: 'auto'
                    }
                }}
            >
                <Stack gap="md" style={{ minHeight: 'fit-content' }}>
                    {/* Country/Language for Mobile */}
                    <Group justify="center">
                        <Menu shadow="lg" width={200}>
                            <Menu.Target>
                                <Button
                                    variant="light"
                                    color="customGreen"
                                    leftSection={<span style={{ fontSize: '16px' }}>{getCurrentCountryFlag()}</span>}
                                    fullWidth
                                    justify="flex-start"
                                    style={{
                                        borderRadius: '12px',
                                        padding: '12px 16px',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        lineHeight: 1.6,
                                        minHeight: '48px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {t('countrySelector.label')}
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown style={{ borderRadius: '12px', padding: '8px' }}>
                                <Menu.Label style={{ fontWeight: 600, fontSize: '14px' }}>
                                    {t('menu.countries')}
                                </Menu.Label>
                                <Menu.Item
                                    onClick={() => { setCountry('NL'); setDrawerOpened(false); }}
                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                >
                                    🇳🇱 {t('country.NL')}
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() => { setCountry('BE'); setDrawerOpened(false); }}
                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                >
                                    🇧🇪 {t('country.BE')}
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() => { setCountry('US'); setDrawerOpened(false); }}
                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                >
                                    🇺🇸 {t('country.US')}
                                </Menu.Item>
                                <Divider my="xs" />
                                <Menu.Label style={{ fontWeight: 600, fontSize: '14px' }}>
                                    {t('menu.languages')}
                                </Menu.Label>
                                <Menu.Item
                                    onClick={() => { setLanguage('en'); setDrawerOpened(false); }}
                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                >
                                    🇺🇸 English
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() => { setLanguage('nl'); setDrawerOpened(false); }}
                                    style={{ borderRadius: '8px', margin: '2px 0' }}
                                >
                                    🇳🇱 Nederlands
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </Group>

                    {/* Notifications for Mobile */}
                    {user && (
                        <Button
                            variant="light"
                            color="customGreen"
                            leftSection={<ThemedIcon icon={IconBell} size={16} />}
                            rightSection={unreadCount > 0 ? (
                                <Badge
                                    size="xs"
                                    variant="filled"
                                    color="red"
                                    style={{
                                        minWidth: '18px',
                                        height: '18px',
                                        fontSize: '10px',
                                        borderRadius: '9px',
                                        fontWeight: 600
                                    }}
                                >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </Badge>
                            ) : undefined}
                            onClick={() => {
                                navigate('/notifications')
                                setDrawerOpened(false)
                            }}
                            fullWidth
                            justify="flex-start"
                            style={{
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1.6,
                                minHeight: '48px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {t('nav.notifications')}
                        </Button>
                    )}

                    {/* Admin Link for Mobile */}
                    {user && isAdmin && (
                        <Button
                            variant="light"
                            color="customGreen"
                            leftSection={<ThemedIcon icon={IconShield} size={16} />}
                            onClick={() => {
                                navigate('/admin')
                                setDrawerOpened(false)
                            }}
                            fullWidth
                            justify="flex-start"
                            style={{
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1.6,
                                minHeight: '48px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {t('nav.admin')}
                        </Button>
                    )}

                    <Text size="xs" fw={600} c="dimmed" px="sm" mt="md" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '11px' }}>
                        {t('nav.user')}
                    </Text>

                    {/* User Actions */}
                    {user ? (
                        <>
                            <Button
                                variant="light"
                                color="customGreen"
                                leftSection={<ThemedIcon icon={IconHeart} size={16} />}
                                onClick={() => {
                                    navigate('/wishlist')
                                    setDrawerOpened(false)
                                }}
                                fullWidth
                                justify="flex-start"
                                style={{
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {t('nav.wishlist')}
                            </Button>

                            <Button
                                variant="light"
                                color="customGreen"
                                leftSection={<ThemedIcon icon={IconSettings} size={16} />}
                                onClick={() => {
                                    navigate('/settings')
                                    setDrawerOpened(false)
                                }}
                                fullWidth
                                justify="flex-start"
                                style={{
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {t('nav.settings')}
                            </Button>

                            <Button
                                variant="light"
                                leftSection={<IconLogout size={16} color="red" />}
                                onClick={() => {
                                    handleLogout()
                                    setDrawerOpened(false)
                                }}
                                fullWidth
                                justify="flex-start"
                                color="red"
                                style={{
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {t('nav.logout')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="gradient"
                                gradient={{ from: 'customGreen', to: 'customGreen', deg: 135 }}
                                leftSection={<IconLogin size={16} color="white" />}
                                onClick={() => {
                                    navigate('/login')
                                    setDrawerOpened(false)
                                }}
                                fullWidth
                                justify="flex-start"
                                style={{
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1.6,
                                    minHeight: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                                }}
                            >
                                {t('auth.loginOrSignup')}
                            </Button>
                        </>
                    )}

                    {/* Browse Section for Mobile */}
                    <Text size="xs" fw={600} c="dimmed" px="sm" mt="md" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '11px' }}>
                        {t('nav.browse')}
                    </Text>

                    <Button
                        variant="light"
                        color="customGreen"
                        leftSection={<ThemedIcon icon={IconDeviceGamepad2} size={16} />}
                        component={Link}
                        to="/games"
                        onClick={() => setDrawerOpened(false)}
                        fullWidth
                        justify="flex-start"
                        style={{
                            borderRadius: '12px',
                            padding: '12px 16px',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: 1.6,
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {t('nav.games')}
                    </Button>

                    <Button
                        variant="light"
                        color="customGreen"
                        leftSection={<ThemedIcon icon={IconBuildingStore} size={16} />}
                        component={Link}
                        to="/stores"
                        onClick={() => setDrawerOpened(false)}
                        fullWidth
                        justify="flex-start"
                        style={{
                            borderRadius: '12px',
                            padding: '12px 16px',
                            fontWeight: 500,
                            fontSize: '14px',
                            lineHeight: 1.6,
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {t('nav.stores')}
                    </Button>

                    {/* Platforms Section for Mobile */}
                    <Text size="xs" fw={600} c="dimmed" px="sm" mt="md" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '11px' }}>
                        {t('nav.platforms')}
                    </Text>
                    {platforms?.map((platform) => (
                        <Button
                            key={platform.slug}
                            variant="light"
                            leftSection={<ThemedIcon icon={IconDeviceGamepad2} size={16} />}
                            component={Link}
                            to={`/platform/${platform.slug}`}
                            onClick={() => setDrawerOpened(false)}
                            fullWidth
                            justify="flex-start"
                            style={{
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1.6,
                                minHeight: '48px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {platform.name}
                        </Button>
                    ))}
                </Stack>
            </Drawer>
        </>
    )
} 